'use no memo'

import { useMemo } from 'react'
import { Link, Outlet, createFileRoute, useNavigate } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { useChildProjects, useRootProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { usePageLayout } from '@/hooks/usePageLayout'
import TaskSidebar from '@/components/app/sidebars/task'
import { LocationSwatch } from '@/components/app/sidebars/spaces/SpaceSwatch'
import { StarToggle } from '@/components/app/StarToggle'
import { ProjectProvider } from '@/routes/features/projects/context/ProjectContext'
import { ProjectTabContent } from '@/routes/features/projects/components/ProjectTabContent'
import { useLayoutRouteState } from '@/routes/features/projects/hooks/useProjectChildRouteType'

type ProjectBaseTab = 'tasks' | 'pages' | 'cycles'
const PROJECT_BASE_TABS: Array<ProjectBaseTab> = ['tasks', 'pages', 'cycles']

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey/projects/$projectKey',
)({
  component: SpaceProjectDetailsPage,
  // Use `projectTab` instead of `tab` to avoid colliding with the parent
  // SpaceDetailsPage's `tab` search param (which has a different value union).
  validateSearch: (search: Record<string, unknown>): { projectTab?: ProjectBaseTab } => {
    const tab = search.projectTab as string | undefined
    if (tab && PROJECT_BASE_TABS.includes(tab as ProjectBaseTab)) {
      return { projectTab: tab as ProjectBaseTab }
    }
    return {}
  },
})

export default function SpaceProjectDetailsPage() {
  const { workspaceSlug, spaceKey, projectKey } = Route.useParams()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((state) => state.workspaceId)

  const basePath = `/${workspaceSlug}/spaces/${spaceKey}/projects/${projectKey}`
  const { isActive, childRouteType } = useLayoutRouteState(basePath)

  const { projects: rootProjects, isLoading: spacesLoading } = useRootProjects()

  const space = useMemo<ProjectDocument | null>(() => {
    return rootProjects?.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
      if (workspaceId && p.envId !== workspaceId) return false
      return true
    }) ?? null
  }, [rootProjects, spaceKey, workspaceId])

  const { projects: childProjects } = useChildProjects(space?.id)

  const project = useMemo<ProjectDocument | null>(() => {
    return childProjects?.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== projectKey.toLowerCase()) return false
      return true
    }) ?? null
  }, [childProjects, projectKey])

  // Breadcrumb
  const breadcrumbContent = useMemo(() => {
    if (!space || !project) return null
    return (
      <>
        <Link
          to="/$workspaceSlug/spaces/$spaceKey"
          params={{ workspaceSlug, spaceKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <LocationSwatch location={space} size={14} />
          <span className="truncate">{space.name}</span>
        </Link>
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <Link
          to="/$workspaceSlug/spaces/$spaceKey/projects/$projectKey"
          params={{ workspaceSlug, spaceKey, projectKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          {/* The project's own mark, as in the sidebar (design 33); no "Projects" filler. */}
          <LocationSwatch location={project} size={14} />
          <span className="truncate">{project.name || 'Untitled project'}</span>
        </Link>
        <StarToggle entityType="project" entityId={project.id} size={14} />
      </>
    )
  }, [space, project, workspaceSlug, spaceKey, projectKey])

  usePageLayout({
    sidebarContent: <TaskSidebar />,
    ...(childRouteType === 'other' || !breadcrumbContent
      ? {}
      : { breadcrumb: breadcrumbContent }),
  }, [space?.id, space?.name, space?.icon, space?.iconType, space?.iconColor, project?.id, project?.name, project?.icon, project?.iconType, project?.iconColor, workspaceSlug, spaceKey, projectKey, childRouteType])

  // During route transitions the router updates location before unmounting
  // stale child layouts — bail out so our Navigate doesn't race the parent's.
  if (!isActive) return null

  if (childRouteType === 'other' || childRouteType === 'views') return <Outlet />

  if (spacesLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!space || !project) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Project not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <ProjectProvider projectId={project.id}>
      <ProjectTabContent
        projectId={project.id}
        projectKey={projectKey}
        workspaceSlug={workspaceSlug}
        spaceKey={spaceKey}
        onCycleClick={(cycleId) => {
          navigate({
            to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/cycles/$cycleId',
            params: { workspaceSlug, spaceKey, projectKey, cycleId },
          })
        }}
        onPageClick={(slug) => {
          navigate({
            to: '/$workspaceSlug/spaces/$spaceKey/projects/$projectKey/pages/$pageSlug',
            params: { workspaceSlug, spaceKey, projectKey, pageSlug: slug },
          })
        }}
      />
    </ProjectProvider>
  )
}
